import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Text, 
  Flex, 
  Badge, 
  useColorModeValue, 
  Tooltip,
  IconButton,
  HStack,
  Spacer,
  Divider,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { PhoneIcon, EditIcon, ChatIcon, CheckIcon, CalendarIcon, InfoIcon, WarningIcon, CloseIcon } from '@chakra-ui/icons';
import moment from 'moment';
import { useSocket } from '../../../../contexts/SocketContext';
import { formatIndianCurrency } from '../../../../utils/formatters';
import { Draggable } from 'react-beautiful-dnd';
import NewKBRecordCallModal from '../../NewKB/components/NewKBRecordCallModal';
import NewKBLeadModal from '../../NewKB/components/NewKBLeadModal';

/**
 * Lead card component for TanStack implementation of Kanban board
 * @param {Object} props - Component props
 * @param {Object} props.lead - Lead data
 * @param {number} props.index - Index of lead in the list (for drag and drop)
 * @param {boolean} props.dragDisabled - Whether drag functionality is disabled
 * @returns {React.ReactElement} - Lead card component
 */
const TanLeadCard = ({ lead, index, dragDisabled = false }) => {
  const { socket } = useSocket();
  
  // Debug - log lead info when card is rendered
  console.log(`Rendering TanLeadCard: ID=${lead.leadId || lead._id}, stage=${lead.stage}`);
  
  // Local state for real-time updates
  const [localCallCount, setLocalCallCount] = useState(lead.callCount || 0);
  const [localCommentCount, setLocalCommentCount] = useState(lead.commentCount || 0);
  const [localTaskCount, setLocalTaskCount] = useState(lead.taskCount || 0);
  const [localLastCallTime, setLocalLastCallTime] = useState(lead.lastCallTime || null);
  const [localNextFollowUpTime, setLocalNextFollowUpTime] = useState(
    lead.nextFollowUpTime || null
  );
  
  // Modal state
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Track last event timestamp for each lead to prevent race conditions
  const lastEventTimestampRef = useRef({});

  // Create a key that represents all lead data to detect any changes
  const leadDataKey = JSON.stringify({
    id: lead.leadId || lead._id,
    name: lead.brideAndGroomName,
    callCount: lead.callCount,
    commentCount: lead.commentCount,
    taskCount: lead.taskCount,
    followUpTime: lead.nextFollowUpTime,
    lastUpdated: lead._lastUpdated,
    updateKey: lead._updateKey,
    budget: lead.budget?.amount,
    phone: lead.phone,
    email: lead.email,
    stage: lead.stage?._id
  });

  // Update local state whenever lead property changes
  useEffect(() => {
    // For call count, check if the incoming value is valid and higher than current
    if (lead.callCount !== undefined && lead.callCount !== null) {
      const incomingCount = Number(lead.callCount);
      // Only update if incoming count is higher than what we have locally
      if (incomingCount >= localCallCount) {
        setLocalCallCount(incomingCount);
      }
    }
    
    // For comment count, apply the same logic
    if (lead.commentCount !== undefined && lead.commentCount !== null) {
      setLocalCommentCount(Number(lead.commentCount));
    }
    
    // For task count, apply the same logic
    if (lead.taskCount !== undefined && lead.taskCount !== null) {
      setLocalTaskCount(Number(lead.taskCount));
    }
    
    // Always update follow-up time and last call time
    setLocalNextFollowUpTime(lead.nextFollowUpTime || null);
    setLocalLastCallTime(lead.lastCallTime || null);
  }, [leadDataKey, localCallCount, localCommentCount, localTaskCount]);
  
  // Handle successful call recording
  const handleCallSuccess = (updatedLead) => {
    // Update follow-up time if available
    if (updatedLead.nextFollowUpTime) {
      setLocalNextFollowUpTime(updatedLead.nextFollowUpTime);
    }
  };

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    // Handle call:added events
    const handleCallAdded = (data) => {
      // Check if this event is for our lead
      if (data.leadId === lead.leadId || data.leadId === lead._id) {
        // Track event timestamp to handle out-of-order events
        const leadId = lead.leadId || lead._id;
        const eventTime = new Date(data.timestamp || Date.now()).getTime();
        const lastTime = lastEventTimestampRef.current[leadId] || 0;
        
        // Only process if this event is newer than the last one we processed
        // or if we're processing a new callCount that's higher than what we have
        const newCount = data.callCount !== undefined ? Number(data.callCount) : null;
        const shouldProcessEvent = eventTime > lastTime || (newCount !== null && newCount > localCallCount);
        
        if (shouldProcessEvent) {
          // Update the timestamp reference
          lastEventTimestampRef.current[leadId] = eventTime;
          
          // Update follow-up time if included in the event
          if (data.rescheduledTo) {
            setLocalNextFollowUpTime(data.rescheduledTo);
          }
          
          // Update last call time if included
          if (data.lastCallTime) {
            setLocalLastCallTime(data.lastCallTime);
          }
          
          // Always force a call count update from the event
          if (data.callCount !== undefined) {
            setLocalCallCount(newCount);
          } else {
            // Fallback increment for backward compatibility
            setLocalCallCount(prevCount => {
              const incrementedCount = Number(prevCount || 0) + 1;
              return incrementedCount;
            });
          }
        }
      }
    };

    // Handle lead:updated events
    const handleLeadUpdated = (data) => {
      // Handle both formats: direct lead update or {leadId, lead} format
      const updatedLead = data.lead || data;
      const businessLeadId = data.leadId || (updatedLead && updatedLead.leadId);
      
      // Track event timestamp to prevent race conditions
      const leadId = lead.leadId || lead._id;
      const eventTime = new Date(data.eventTime || data.timestamp || Date.now()).getTime();
      const lastTime = lastEventTimestampRef.current[leadId] || 0;
      
      // Check if this update is for our lead using business leadId
      if (businessLeadId === lead.leadId) {
        // Only process if this event is newer than the last one we processed
        // or if we're seeing a higher call count
        const newCount = (updatedLead && updatedLead.callCount !== undefined) ? 
          Number(updatedLead.callCount) : 
          (data.callCount !== undefined ? Number(data.callCount) : null);

        const shouldProcessEvent = eventTime > lastTime || (newCount !== null && newCount > localCallCount);
        
        if (shouldProcessEvent) {
          // Update the timestamp reference
          lastEventTimestampRef.current[leadId] = Math.max(eventTime, lastTime);

          // Update counts if included
          if (updatedLead && updatedLead.callCount !== undefined) {
            setLocalCallCount(Number(updatedLead.callCount));
          } else if (data.callCount !== undefined) {
            setLocalCallCount(Number(data.callCount));
          }
          
          if (updatedLead && updatedLead.commentCount !== undefined) {
            setLocalCommentCount(Number(updatedLead.commentCount));
          } else if (data.commentCount !== undefined) {
            setLocalCommentCount(Number(data.commentCount));
          }
        
          if (updatedLead && updatedLead.taskCount !== undefined) {
            setLocalTaskCount(Number(updatedLead.taskCount));
          } else if (data.taskCount !== undefined) {
            setLocalTaskCount(Number(data.taskCount));
          }
          
          // Update follow-up time if included
          if (updatedLead && updatedLead.nextFollowUpTime !== undefined) {
            setLocalNextFollowUpTime(updatedLead.nextFollowUpTime);
          } else if (data.nextFollowUpTime !== undefined) {
            setLocalNextFollowUpTime(data.nextFollowUpTime);
          }
        }
      }
    };

    // Handle backup call count update events
    const handleCallCountUpdated = (data) => {
      if (data.leadId === lead.leadId || data.leadId === lead._id) {
        // For backup events, only use if they have a higher count
        if (data.callCount !== undefined) {
          const newCount = Number(data.callCount);
          // Only update if the new count is higher than our current local count
          if (newCount > localCallCount) {
            setLocalCallCount(newCount);
          }
        }
      }
    };
    
    // Register event listeners
    socket.on('call:added', handleCallAdded);
    socket.on('lead:updated', handleLeadUpdated);
    socket.on('callCount:updated', handleCallCountUpdated);

    // Cleanup function
    return () => {
      socket.off('call:added', handleCallAdded);
      socket.off('lead:updated', handleLeadUpdated);
      socket.off('callCount:updated', handleCallCountUpdated);
    };
  }, [socket, lead.leadId, lead._id, localCallCount]);

  // Format relative time for follow-up
  const getRelativeTime = (timeString) => {
    if (!timeString) return 'No follow-up';
    
    const time = moment(timeString);
    const now = moment();
    
    if (time.isBefore(now)) {
      return `Overdue: ${time.fromNow()}`;
    }
    return `Follow-up: ${time.fromNow()}`;
  };

  // Determine if follow-up is overdue or missing
  const isOverdue = localNextFollowUpTime ? 
    moment(localNextFollowUpTime).isBefore(moment()) : false;
  const hasNoFollowUp = !localNextFollowUpTime;
  
  // Card styling based on state
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const followUpColor = hasNoFollowUp || isOverdue ? 'red.500' : 'green.500';

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('MMM D, YYYY');
  };
  
  // Get display name - could be brideAndGroomName or name
  const displayName = lead.brideAndGroomName || 
    (typeof lead.name === 'string' ? lead.name : 
     typeof lead.name === 'object' && lead.name?.name ? lead.name.name : 'Unnamed Lead');
  
  // Get first event date if available
  const firstEventDate = lead.eventDates && lead.eventDates.length > 0 ? lead.eventDates[0] : null;
  
  // Get city if available
  const city = lead.locations && lead.locations.cities && lead.locations.cities.length > 0 ? 
    lead.locations.cities[0] : null;
  
  // Get budget if available
  const budget = lead.budget && lead.budget.amount ? 
    formatIndianCurrency(lead.budget.amount) : null;

  return (
    <>
      <Draggable 
        draggableId={String(lead._uniqueId || lead.leadId || lead._id || `temp-lead-${index}`)} 
        index={index}
        isDragDisabled={dragDisabled || (!lead.leadId && !lead._id)}
      >
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            bg={bgColor}
            p={2}
            borderWidth="1px"
            borderRadius="md"
            borderColor={borderColor}
            mb={2}
            boxShadow={snapshot.isDragging ? "md" : "none"}
            _hover={{ borderColor: "blue.400" }}
            data-testid={`lead-card-${lead.leadId || lead._id}`}
          >
            {/* Top Row: Name and Action Buttons */}
            <Flex justify="space-between" align="center" mb={0.5}>
              {/* Lead Name */}
              <Text fontWeight="semibold" fontSize="xs" isTruncated maxWidth="75%">
                {displayName}
              </Text>
              
              {/* Action Buttons */}
              <Flex>
                <Tooltip 
                  hasArrow 
                  openDelay={300}
                  label={
                    <Box>
                      <Text fontWeight="bold">Record Call</Text>
                      {localLastCallTime ? (
                        <Text fontSize="xs" mt={1}>
                          Last call: {moment(localLastCallTime).format('MMM D, YYYY h:mm A')}
                        </Text>
                      ) : localCallCount > 0 ? (
                        <Text fontSize="xs" mt={1}>Last call time not available</Text>
                      ) : (
                        <Text fontSize="xs" mt={1}>No previous calls</Text>
                      )}
                    </Box>
                  }
                >
                  <IconButton
                    icon={<PhoneIcon boxSize={3} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    mr={1}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCallModalOpen(true);
                    }}
                  />
                </Tooltip>
                
                <Tooltip label="Edit Lead">
                  <IconButton
                    icon={<EditIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditModalOpen(true);
                    }}
                  />
                </Tooltip>
              </Flex>
            </Flex>
            
            {/* Second Row: Follow-up Time and No Calls Alert */}
            <Flex justify="space-between" align="center" mb={1}>
              <Text 
                fontSize="xs" 
                color={followUpColor} 
                fontWeight={isOverdue ? "bold" : "normal"}
              >
                {getRelativeTime(localNextFollowUpTime)}
              </Text>
              
              {/* Show No Calls Yet message when call count is 0 */}
              {localCallCount === 0 && (
                <Text
                  fontSize="xs"
                  color="red.500"
                  fontWeight="medium"
                >
                  No Calls Yet
                </Text>
              )}
            </Flex>
            
            {/* Additional Lead Info - Only shown if available */}
            {(firstEventDate || city || budget) && (
              <Flex fontSize="xs" mb={1} justify="flex-start" align="center" wrap="wrap" gap={2}>
                {firstEventDate && (
                  <Flex align="center">
                    <CalendarIcon mr={1} color="blue.500" boxSize={2.5} />
                    <Text color="gray.600" fontSize="xs">{formatDate(firstEventDate)}</Text>
                  </Flex>
                )}
                
                {city && (
                  <Flex align="center">
                    <InfoIcon mr={1} color="purple.500" boxSize={2.5} />
                    <Text color="gray.600" fontSize="xs">{city}</Text>
                  </Flex>
                )}
                
                {budget && (
                  <Badge colorScheme="green" variant="subtle" fontSize="xs">
                    {budget}
                  </Badge>
                )}
              </Flex>
            )}
            
            {/* Badge Row */}
            <Flex wrap="wrap" gap={1} mb={1}>
              <Badge colorScheme="blue" size="sm" fontSize="xs">
                <Flex align="center">
                  <PhoneIcon mr={1} boxSize={2.5} />
                  {localCallCount}
                </Flex>
              </Badge>
              
              {/* Alert Icon for Incomplete Requirements */}
              {lead.incompleteRequirements && lead.incompleteRequirements.length > 0 && (
                <Tooltip label={
                  <Box p={1}>
                    <Text fontWeight="bold" mb={1}>Missing Requirements:</Text>
                    <List spacing={1} fontSize="xs">
                      {lead.incompleteRequirements.map((req, idx) => (
                        <ListItem key={idx}>
                          <ListIcon as={CloseIcon} color="red.500" boxSize={2} />
                          {req.name}: {req.description}
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                } hasArrow placement="top">
                  <Badge colorScheme="red" size="sm" fontSize="xs">
                    <Flex align="center">
                      <WarningIcon mr={1} boxSize={2.5} />
                      {lead.incompleteRequirements.length}
                    </Flex>
                  </Badge>
                </Tooltip>
              )}
              
              <Badge colorScheme="teal" size="sm" fontSize="xs">
                <Flex align="center">
                  <ChatIcon mr={1} boxSize={2.5} />
                  {localCommentCount}
                </Flex>
              </Badge>
              
              <Badge colorScheme="orange" size="sm" fontSize="xs">
                <Flex align="center">
                  <CheckIcon mr={1} boxSize={2.5} />
                  {localTaskCount}
                </Flex>
              </Badge>
              
              {lead.tags && lead.tags.length > 0 && (
                <Badge colorScheme="purple" size="sm" fontSize="xs">
                  {lead.tags[0]}
                </Badge>
              )}
            </Flex>
            
            {/* Bottom Row: Added Date and Priority */}
            <Flex justify="space-between" align="center" fontSize="xs">
              <Text color="gray.500">
                Added: {formatDate(lead.dateAdded)}
              </Text>
              
              {lead.priority && (
                <Badge colorScheme={
                  lead.priority === 'high' ? 'red' : 
                  lead.priority === 'medium' ? 'orange' : 'green'
                } size="sm" fontSize="xs">
                  {lead.priority}
                </Badge>
              )}
            </Flex>
          </Box>
        )}
      </Draggable>
      
      {/* Modals */}
      {isCallModalOpen && (
        <NewKBRecordCallModal
          isOpen={isCallModalOpen}
          onClose={() => setIsCallModalOpen(false)}
          lead={lead}
          onSuccess={handleCallSuccess}
        />
      )}
      
      {isEditModalOpen && (
        <NewKBLeadModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          leadId={lead.leadId}
          onLeadAdded={(updatedLead) => {
            // We rely on the QueryClient to handle updates
          }}
        />
      )}
    </>
  );
};

// Use React.memo to prevent unnecessary re-renders
const MemoizedTanLeadCard = React.memo(TanLeadCard, (prevProps, nextProps) => {
  // If the lead ID is different, definitely re-render
  if (prevProps.lead.leadId !== nextProps.lead.leadId) return false;
  
  // If key properties are the same, we can skip re-rendering
  const prevKey = JSON.stringify({
    name: prevProps.lead.brideAndGroomName,
    callCount: prevProps.lead.callCount,
    commentCount: prevProps.lead.commentCount,
    taskCount: prevProps.lead.taskCount,
    stage: typeof prevProps.lead.stage === 'object' ? prevProps.lead.stage._id : prevProps.lead.stage,
    _updateKey: prevProps.lead._updateKey,
    _lastUpdated: prevProps.lead._lastUpdated
  });
  
  const nextKey = JSON.stringify({
    name: nextProps.lead.brideAndGroomName,
    callCount: nextProps.lead.callCount,
    commentCount: nextProps.lead.commentCount,
    taskCount: nextProps.lead.taskCount,
    stage: typeof nextProps.lead.stage === 'object' ? nextProps.lead.stage._id : nextProps.lead.stage,
    _updateKey: nextProps.lead._updateKey,
    _lastUpdated: nextProps.lead._lastUpdated
  });
  
  // Return true if the keys are the same (meaning no re-render needed)
  return prevKey === nextKey;
});

export default MemoizedTanLeadCard;
